import { useEffect, useState, type ReactNode } from 'react';
import { API_BASE_URL } from '../constants';
import { SidebarIcon } from '../components/SidebarIcon';
import { ProfileAccountDeletion } from '../components/ProfileAccountDeletion';
import './PortalSectionLayout.css';
import './PortalSectionInteractions.css';

// /api/user/update-full

export interface FullUser {
  id: string;
  userName?: string;
  firstName?: string;
  familyName?: string;
  email?: string;
  mobile?: string;
  customerId?: string;

  extId?: string;
  salutation?: string;
  title?: string;
  zipCode?: string;
  town?: string;
  addressLines?: string[];
  languages?: string[];
  dateOfBirth?: string | number | null;
  // phones: JSON
  // comment: JSON
}

interface ProfileFormState {
  salutation: string;
  dateOfBirth: string;
  firstName: string;
  familyName: string;
  email: string;
  mobile: string;
  streetAndNumber: string;
  zipCode: string;
  town: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const emptyFormState: ProfileFormState = {
  salutation: '',
  dateOfBirth: '',
  firstName: '',
  familyName: '',
  email: '',
  mobile: '',
  streetAndNumber: '',
  zipCode: '',
  town: '',
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const formatOptionalDate = (value?: string) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const mapUserToForm = (user?: FullUser): ProfileFormState => ({
  salutation: String(user?.salutation ?? '').trim(),
  dateOfBirth: formatOptionalDate(String(user?.dateOfBirth ?? '').trim()),
  firstName: String(user?.firstName ?? '').trim(),
  familyName: String(user?.familyName ?? '').trim(),
  email: String(user?.email ?? '').trim(),
  mobile: String(user?.mobile ?? '').trim(),
  streetAndNumber: Array.isArray(user?.addressLines) ? String(user?.addressLines[0] ?? '').trim() : '',
  zipCode: String(user?.zipCode ?? '').trim(),
  town: String(user?.town ?? '').trim(),
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
});

const normalizeFieldValue = (value: string) => value.trim();
const writeFlag = false;

const profileFieldsChanged = (currentForm: ProfileFormState, initialForm: ProfileFormState) =>
  normalizeFieldValue(currentForm.salutation) !== normalizeFieldValue(initialForm.salutation) ||
  normalizeFieldValue(currentForm.dateOfBirth) !== normalizeFieldValue(initialForm.dateOfBirth) ||
  normalizeFieldValue(currentForm.firstName) !== normalizeFieldValue(initialForm.firstName) ||
  normalizeFieldValue(currentForm.familyName) !== normalizeFieldValue(initialForm.familyName) ||
  normalizeFieldValue(currentForm.email) !== normalizeFieldValue(initialForm.email) ||
  normalizeFieldValue(currentForm.mobile) !== normalizeFieldValue(initialForm.mobile) ||
  normalizeFieldValue(currentForm.streetAndNumber) !== normalizeFieldValue(initialForm.streetAndNumber) ||
  normalizeFieldValue(currentForm.zipCode) !== normalizeFieldValue(initialForm.zipCode) ||
  normalizeFieldValue(currentForm.town) !== normalizeFieldValue(initialForm.town);

const parseDateInput = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  const dotSeparatedMatch = trimmedValue.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dotSeparatedMatch) {
    const [, day, month, year] = dotSeparatedMatch;
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));

    if (
      parsedDate.getFullYear() === Number(year) &&
      parsedDate.getMonth() === Number(month) - 1 &&
      parsedDate.getDate() === Number(day)
    ) {
      return parsedDate.getTime();
    }

    return Number.NaN;
  }

  const parsedDate = new Date(trimmedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return Number.NaN;
  }

  return parsedDate.getTime();
};

const SectionIcon = ({ type }: { type: 'profile' | 'contact' | 'address' | 'password' }) => {
  const iconClassName = 'h-4 w-4';

  if (type === 'profile') {
    return (
      <svg viewBox="0 0 24 24" className={iconClassName} fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="8" r="3" />
        <path d="M6.5 18c1.4-2.6 3.3-4 5.5-4s4.1 1.4 5.5 4" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === 'contact') {
    return <SidebarIcon name="phone" className={iconClassName} />;
  }

  if (type === 'address') {
    return (
      <svg viewBox="0 0 24 24" className={iconClassName} fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 20c3.2-3.4 4.8-6.1 4.8-8.4A4.8 4.8 0 1 0 7.2 11.6C7.2 13.9 8.8 16.6 12 20Z" />
        <circle cx="12" cy="11.2" r="1.8" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className={iconClassName} fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="7" y="10" width="10" height="9" rx="2" />
      <path d="M9 10V8a3 3 0 1 1 6 0v2" strokeLinecap="round" />
    </svg>
  );
};

const SectionCard = ({
  title,
  iconType,
  children,
}: {
  title: string;
  iconType: 'profile' | 'contact' | 'address' | 'password';
  children: ReactNode;
}) => (
  <section className="portal-section-card rounded-[34px] border border-[#f0ebe3] bg-white px-7 py-7 shadow-[0_20px_50px_rgba(30,41,59,0.03)] sm:px-9 sm:py-8 xl:px-11">
    <div className="mb-7 flex items-center gap-3">
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#fbf7ef] text-[#bda06a]">
        <SectionIcon type={iconType} />
      </span>
      <h2 className="text-[25px] font-semibold tracking-[-0.02em] text-slate-700">{title}</h2>
    </div>
    {children}
  </section>
);

const Field = ({
  label,
  value,
  onChange,
  type = 'text',
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  disabled?: boolean;
}) => (
  <label className="block">
    <span className="mb-2 block text-[11px] font-medium text-slate-300">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      className="h-12 w-full rounded-2xl border border-[#f1ede7] bg-[#fcfbf8] px-4 text-[15px] text-slate-600 outline-none transition disabled:cursor-not-allowed disabled:text-slate-400 focus:border-[#dac49e] focus:bg-white"
    />
  </label>
);

export function ProfilePage() {
  const [formData, setFormData] = useState<ProfileFormState>(emptyFormState);
  const [initialFormData, setInitialFormData] = useState<ProfileFormState>(emptyFormState);
  const [fullUser, setFullUser] = useState<FullUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;

    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('loginUserId')?.trim();

      if (!token || !userId) {
        if (!ignore) {
          setError('Benutzerdaten konnten nicht geladen werden.');
          setLoading(false);
        }
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/user/full/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Profile request failed: ${response.status}`);
        }

        const user = (await response.json()) as FullUser;
        if (!ignore) {
          setFormData(mapUserToForm(user));
          setInitialFormData(mapUserToForm(user));
          setFullUser(user);
          setError('');
        }
      } catch (fetchError) {
        console.error('Failed to load profile', fetchError);
        if (!ignore) {
          setError('Benutzerdaten konnten nicht geladen werden.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchUser();

    return () => {
      ignore = true;
    };
  }, []);

  const updateField = (field: keyof ProfileFormState, value: string) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const updateFullUser = async () => {
    const token = localStorage.getItem('token');

    if (!token || !fullUser?.id) {
      setSaveError('Profil konnte nicht gespeichert werden. Bitte erneut anmelden.');
      return false;
    }

    const parsedDateOfBirth = parseDateInput(formData.dateOfBirth);

    if (Number.isNaN(parsedDateOfBirth)) {
      setSaveError('Geburtsdatum ist ungültig.');
      return false;
    }

    const response = await fetch(`${API_BASE_URL}/user/update-full`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...fullUser,
        salutation: formData.salutation.trim(),
        firstName: formData.firstName.trim(),
        familyName: formData.familyName.trim(),
        email: formData.email.trim(),
        mobile: formData.mobile.trim(),
        zipCode: formData.zipCode.trim(),
        town: formData.town.trim(),
        addressLines: formData.streetAndNumber.trim() ? [formData.streetAndNumber.trim()] : [],
        dateOfBirth: parsedDateOfBirth,
      }),
    });

    if (!response.ok) {
      throw new Error(`Update full user failed: ${response.status}`);
    }

    const updatedUser = (await response.json().catch(() => null)) as FullUser | null;
    const nextFullUser = updatedUser ?? {
      ...fullUser,
      salutation: formData.salutation.trim(),
      firstName: formData.firstName.trim(),
      familyName: formData.familyName.trim(),
      email: formData.email.trim(),
      mobile: formData.mobile.trim(),
      zipCode: formData.zipCode.trim(),
      town: formData.town.trim(),
      addressLines: formData.streetAndNumber.trim() ? [formData.streetAndNumber.trim()] : [],
      dateOfBirth: parsedDateOfBirth,
    };
    const nextFormData = {
      ...formData,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };

    setFullUser(nextFullUser);
    setFormData(nextFormData);
    setInitialFormData({
      ...mapUserToForm(nextFullUser),
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });

    return true;
  };

  const handleChangePassword = async () => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('loginUserId')?.trim();
    const currentPassword = formData.currentPassword.trim();
    const newPassword = formData.newPassword.trim();
    const confirmPassword = formData.confirmPassword.trim();

    if (!token || !userId) {
      setSaveError('Passwort konnte nicht geändert werden. Bitte erneut anmelden.');
      return false;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      setSaveError('Bitte füllen Sie alle Passwortfelder aus.');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setSaveError('Die neuen Passwörter stimmen nicht überein.');
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          oldPassword: currentPassword,
          newPassword,
        }),
      });

      if (response.status === 401) {
        setSaveError('Das aktuelle Passwort ist nicht korrekt.');
        return false;
      }

      if (!response.ok) {
        throw new Error(`Change password failed: ${response.status}`);
      }
      setFormData((current) => ({
        ...current,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
      return true;
    } catch (changePasswordError) {
      console.error('Failed to change password', changePasswordError);
      setSaveError('Passwort konnte nicht geändert werden.');
      return false;
    }
  };

  const handleSave = async () => {
    const shouldUpdateProfile = writeFlag && profileFieldsChanged(formData, initialFormData);
    const shouldChangePassword =
      formData.currentPassword.trim() !== '' ||
      formData.newPassword.trim() !== '' ||
      formData.confirmPassword.trim() !== '';

    setSaveError('');
    setSaveSuccess('');

    if (!shouldUpdateProfile && !shouldChangePassword) {
      setSaveSuccess('Keine Änderungen zum Speichern.');
      return;
    }

    setSaving(true);

    try {
      const successMessages: string[] = [];

      if (shouldUpdateProfile) {
        const profileUpdated = await updateFullUser();
        if (!profileUpdated) {
          return;
        }
        successMessages.push('Profil wurde erfolgreich gespeichert.');
      }

      if (shouldChangePassword) {
        const passwordChanged = await handleChangePassword();
        if (!passwordChanged) {
          return;
        }
        successMessages.push('Passwort wurde erfolgreich geändert.');
      }

      if (successMessages.length > 0) {
        setSaveSuccess(successMessages.join(' '));
      }
    } catch (saveProfileError) {
      console.error('Failed to save profile', saveProfileError);
      setSaveError('Profil konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="portal-section-page portal-section-page--profile portal-page-surface text-slate-800">
      <div className="portal-page-content">
        <header className="portal-page-heading">
          <h1 className="text-[44px] font-semibold tracking-[-0.04em] text-slate-700">Mein Profil</h1>
          <p className="mt-1 text-[14px] text-slate-300">
            Sehen Sie Ihre persönlichen Informationen und Kontaktdaten ein.
          </p>
        </header>

        {error ? <p className="mt-6 text-sm text-red-600">{error}</p> : null}
        {loading ? <p className="mt-6 text-sm text-slate-400">Profil wird geladen...</p> : null}
        {saveError ? <p className="mt-4 text-sm text-red-600">{saveError}</p> : null}
        {saveSuccess ? <p className="mt-4 text-sm text-emerald-600">{saveSuccess}</p> : null}

        <div className="portal-section-stack mt-8 space-y-6">
          <SectionCard title="Persönliche Angaben" iconType="profile">
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              <Field label="Anrede" value={formData.salutation} onChange={(value) => updateField('salutation', value)} disabled={!writeFlag} />
              <Field label="Vorname" value={formData.firstName} onChange={(value) => updateField('firstName', value)} disabled={!writeFlag} />
              <Field label="Nachname" value={formData.familyName} onChange={(value) => updateField('familyName', value)} disabled={!writeFlag} />
              <Field label="Geburtsdatum" value={formData.dateOfBirth} onChange={(value) => updateField('dateOfBirth', value)} disabled={!writeFlag} />
            </div>
          </SectionCard>

          <SectionCard title="Kontaktdaten" iconType="contact">
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(360px,1fr)]">
              <Field label="E-Mail Adresse" value={formData.email} onChange={(value) => updateField('email', value)} type="email" disabled={!writeFlag} />
              <Field label="Handynummer" value={formData.mobile} onChange={(value) => updateField('mobile', value)} disabled={!writeFlag} />
            </div>
          </SectionCard>

          <SectionCard title="Adresse" iconType="address">
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-[minmax(0,1.8fr)_minmax(220px,0.7fr)_minmax(260px,1fr)]">
              <div className="md:col-span-2 xl:col-span-1">
                <Field
                  label="Strasse und Hausnummer"
                  value={formData.streetAndNumber}
                  onChange={(value) => updateField('streetAndNumber', value)}
                  disabled={!writeFlag}
                />
              </div>
              <Field label="Postleitzahl" value={formData.zipCode} onChange={(value) => updateField('zipCode', value)} disabled={!writeFlag} />
              <Field label="Ort" value={formData.town} onChange={(value) => updateField('town', value)} disabled={!writeFlag} />
            </div>
          </SectionCard>

          <SectionCard title="Passwort ändern" iconType="password">
            <div className="grid gap-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <Field
                  label="Aktuelles Passwort"
                  value={formData.currentPassword}
                  onChange={(value) => updateField('currentPassword', value)}
                  type="password"
                />
              </div>
              <Field
                label="Neues Passwort"
                value={formData.newPassword}
                onChange={(value) => updateField('newPassword', value)}
                type="password"
              />
              <Field
                label="Passwort wiederholen"
                value={formData.confirmPassword}
                onChange={(value) => updateField('confirmPassword', value)}
                type="password"
              />
            </div>
            <div className="portal-profile__password-actions mt-8 flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || loading}
                className="portal-section-action--gold inline-flex h-16 items-center gap-3 rounded-2xl bg-[#d4bf97] px-8 text-[15px] font-semibold text-white shadow-[0_18px_35px_rgba(212,191,151,0.35)] transition"
              >
                Passwort ändern
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m5 12 4 4L19 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </SectionCard>
        </div>
        <ProfileAccountDeletion />
      </div>
    </div>
  );
}
